import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SEO, articleLd } from "@/lib/seo";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/constants";
import Reveal from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const renderBlock = (block, i) => {
  const t = block.trim();
  if (!t) return null;
  if (t.startsWith("## ")) {
    return (
      <h2 key={i} className="font-display mt-12 text-2xl font-extrabold uppercase tracking-tight text-pearl sm:text-3xl" data-testid="post-h2">
        {t.slice(3)}
      </h2>
    );
  }
  if (t.split("\n").every((l) => l.trim().startsWith("- "))) {
    return (
      <ul key={i} className="mt-6 space-y-2.5" data-testid="post-list">
        {t.split("\n").map((line, j) => (
          <li key={j} className="flex gap-3 text-stonemute">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber" />
            <span>{line.trim().slice(2)}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p key={i} className="mt-6 leading-relaxed text-stonemute" data-testid="post-p">
      {t}
    </p>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 bg-ink px-4 pt-24 text-center">
        <h1 className="font-display text-5xl font-black uppercase text-pearl">Nie znaleziono wpisu</h1>
        <Link to="/blog" data-testid="post-back-blog" className="btn-amber px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em]">
          Wróć do bloga
        </Link>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-[70vh] bg-ink pt-40 text-center text-stonemute">Wczytywanie…</div>;
  }

  const date = post.published_at ? new Date(post.published_at).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <>
      <SEO
        title={post.meta_title || `${post.title} | AD Brikman`}
        description={post.meta_description || post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover}
        type="article"
        jsonLd={articleLd(post)}
      />

      <article className="bg-ink pt-32 lg:pt-40" data-testid="post-page">
        <header className="mx-auto max-w-4xl px-4 sm:px-8">
          <Reveal>
            <Link to="/blog" data-testid="post-back-link" className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber hover:text-amberdeep">
              ← Poradnik brukarski
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="eyebrow mt-8 mb-4">{post.category} {date && `· ${date}`}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-4xl font-black uppercase leading-tight tracking-tight text-pearl sm:text-6xl" data-testid="post-title">
              {post.title}
            </h1>
          </Reveal>
        </header>

        {post.cover && (
          <Reveal delay={0.15}>
            <div className="notch mx-auto mt-12 max-w-5xl overflow-hidden px-4 sm:px-8">
              <img src={post.cover} alt={post.title} className="max-h-[520px] w-full object-cover" data-testid="post-cover" />
            </div>
          </Reveal>
        )}

        <div className="mx-auto max-w-3xl px-4 pb-20 sm:px-8" data-testid="post-content">
          {post.content.split("\n\n").map(renderBlock)}
        </div>

        <section className="border-t border-white/10 bg-graph grain" data-testid="post-cta">
          <div className="paver-bg">
            <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-8">
              <h2 className="font-display text-3xl font-black uppercase text-pearl sm:text-4xl">
                Planujesz podobną <span className="text-amber">nawierzchnię?</span>
              </h2>
              <p className="mt-4 text-stonemute">Doradzimy i wycenimy bezpłatnie. {COMPANY.street}, {COMPANY.postal}</p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <a href={`tel:${COMPANY.phoneRaw}`} data-testid="post-cta-call" className="btn-amber px-8 py-4 font-mono text-sm font-medium tracking-widest">
                  {COMPANY.phone}
                </a>
                <Link to="/kontakt" data-testid="post-cta-kontakt" className="btn-ghost px-8 py-4 font-mono text-sm tracking-widest">
                  Formularz kontaktowy
                </Link>
                <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="post-cta-whatsapp" className="btn-ghost flex items-center gap-2 px-8 py-4 font-mono text-sm tracking-widest">
                  <WhatsAppIcon className="text-[#25D366]" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default BlogPost;
